import React, { useState } from 'react';
import TopBar from 'stash/components/global/TopBar';
import ContentMoatNavBar from 'stash/components/content/ContentMoatNavbar';
import MobileNav from 'stash/components/global/MobileNav';
import SavedMixPrimer from 'stash/components/saved-mix-primer/SavedMixPrimer';
import { useSavedMixPrimer } from 'stash/components/saved-mix-primer/useSavedMixPrimer';
import {
	selectIsAccountlessAppEntryEnabled,
	selectIsContentMoat,
} from 'stash/selectors';
import { useSelector } from 'react-redux';
import useNavOptions from './hooks/useNavOptions';
import { EntitlementsProvider } from 'stash/hooks/useEntitlements';
import settingsPaths from 'stash/routes/SettingsRoutes/paths';
import { isCurrentRoutePartOf, type Paths } from 'stash/utils/routes';
import { FullScreenLayoutProvider } from './FullScreenLayout';

const fullScreenPaths: Paths = [
	['/retire/:screen', 'RetirementScreenRouter'],
	['/account-funding/:screen', 'AccountFundingScreenRouter'],
	['/portfolio-conversion/:screen', 'PortfolioConversionScreenRouter'],
];

// Routes whose root element is locked to the viewport height (h-screen /
// h-[100dvh]) and already subtracts the rail from its own box. Adding the shell
// inset on top would make the page scroll past a layout that is meant not to.
const selfManagedBottomInsetPaths: Paths = [
	['/money-coach', 'Coach'],
	['/invest-an-idea', 'InvestAnIdea'],
];

type DashboardLocation = {
	pathname?: string;
	search?: string;
};

type DashboardHistory = {
	push: (path: string) => void;
};

type DashboardProps = {
	location?: DashboardLocation;
	children?: React.ReactNode;
	history?: DashboardHistory;
};

function Dashboard({ location, children }: DashboardProps) {
	const isContentMoat = useSelector(selectIsContentMoat);
	const isAccountlessAppEntryEnabled = useSelector(selectIsAccountlessAppEntryEnabled);
	const showContentMoat = isContentMoat && !isAccountlessAppEntryEnabled;
	const dashboardReady = useSelector(
		(state: { application: { dashboard?: boolean } }) => state.application.dashboard
	);
	const { selectedIndex, getNavOptions, onNavLinkClick } = useNavOptions();
	const navOptions = getNavOptions();
	const railNavOptions = navOptions.map(
		(nav: { railOption: unknown }) => nav.railOption
	);
	const pathname = location?.pathname ?? '';
	const [isFullScreenRequested, setIsFullScreenRequested] = useState(false);

	const isSettingsRoute = isCurrentRoutePartOf(settingsPaths, pathname);
	const isFullScreenRoute =
		isFullScreenRequested || isCurrentRoutePartOf(fullScreenPaths, pathname);
	const hideNav = isSettingsRoute || isFullScreenRoute;
	const showMobileNav = !showContentMoat && !hideNav;
	const needsBottomInset =
		showMobileNav && !isCurrentRoutePartOf(selfManagedBottomInsetPaths, pathname);
	const savedMixPrimer = useSavedMixPrimer();
	if (savedMixPrimer.visible) {
		return (
			<EntitlementsProvider>
				<SavedMixPrimer
					mixName={savedMixPrimer.mixName}
					onBack={savedMixPrimer.onBack}
					onInvest={savedMixPrimer.onInvest}
					onMakeNewMix={savedMixPrimer.onMakeNewMix}
				/>
			</EntitlementsProvider>
		);
	}

	return (
		<EntitlementsProvider>
			<div className="bg-background min-h-screen">
				{!isFullScreenRoute &&
					(showContentMoat && dashboardReady ? (
						<ContentMoatNavBar />
					) : (
						<TopBar
							navOptions={railNavOptions}
							selectedIndex={selectedIndex}
							onNavLinkClick={onNavLinkClick}
						/>
					))}
				<div className="transition-[background] duration-300 ease-in-out">
					<div className="m-auto">
						{showMobileNav && (
							<MobileNav
								navOptions={railNavOptions}
								selectedIndex={selectedIndex}
								onNavLinkClick={onNavLinkClick}
							/>
						)}
						<main
							className={
								needsBottomInset
									? 'pb-[var(--mobile-nav-height)] gridLarge:pb-0'
									: undefined
							}
						>
							<FullScreenLayoutProvider onFullScreenChange={setIsFullScreenRequested}>
								{children}
							</FullScreenLayoutProvider>
						</main>
					</div>
				</div>
			</div>
		</EntitlementsProvider>
	);
}

export default Dashboard;
